/**
 * Shared helpers for rendering assistant messages from AI SDK `useChat` UIMessages.
 *
 * Engage models stream structured JSON envelopes. Visitors must never see partial
 * prose prefixes, raw JSON, or analysis blobs — only a stable loading state, then
 * formatted prose once the turn completes.
 */

import { FALLBACK_REFUSAL_TEXT } from '@/lib/engage/chat-json-repair';

export type ChatUiMessageLike = {
  role?: string;
  parts?: Array<{ type?: string; state?: unknown; text?: string }>;
};

/** AI SDK `useChat` status values we care about for display gating. */
export type EngageChatStatus = 'submitted' | 'streaming' | 'ready' | 'error' | (string & {});

export const ASSISTANT_THINKING_LABEL = 'Thinking…';

/** After this wait, bouncing dots swap for a bouncing PencilLine. */
export const ENGAGE_THINKING_PENCIL_AFTER_MS = 5_000 as const;

export function shouldShowThinkingPencil(
  elapsedWaitingMs: number,
  delayMs: number = ENGAGE_THINKING_PENCIL_AFTER_MS,
): boolean {
  return elapsedWaitingMs >= delayMs;
}

/** True while a user message is in flight to the model (submitted or streaming). */
export function isChatTurnInFlight(status: EngageChatStatus): boolean {
  return status === 'submitted' || status === 'streaming';
}

/**
 * True when the assistant bubble for this row should stay hidden and show the
 * thinking placeholder instead of parsing partial model output.
 */
export function shouldDeferAssistantBubbleDisplay(opts: {
  role: string | undefined;
  messageIndex: number;
  messageCount: number;
  chatStatus: EngageChatStatus;
}): boolean {
  if (opts.role !== 'assistant') return false;
  if (opts.messageCount === 0) return false;
  if (opts.messageIndex !== opts.messageCount - 1) return false;
  return isChatTurnInFlight(opts.chatStatus);
}

/**
 * Whether visitor-visible assistant text may be extracted from `raw` for this row.
 * Uses `useChat` status as the primary signal; falls back to part `state` when present.
 */
export function isAssistantMessageDisplayReady(opts: {
  role: string | undefined;
  messageIndex: number;
  messageCount: number;
  chatStatus: EngageChatStatus;
  message?: ChatUiMessageLike;
}): boolean {
  if (shouldDeferAssistantBubbleDisplay(opts)) {
    return false;
  }
  if (opts.role !== 'assistant') return true;
  if (opts.messageCount === 0) return true;
  if (opts.messageIndex !== opts.messageCount - 1) return true;

  const textParts = (opts.message?.parts ?? []).filter((p) => p.type === 'text');
  // Turn ended but no text parts were hydrated — surface fallback instead of
  // leaving the bubble stuck on wait chrome.
  if (textParts.length === 0 && opts.chatStatus === 'ready') {
    return true;
  }

  return isAssistantStreamComplete(opts.message ?? {});
}

/** True when all text parts have finished streaming (AI SDK `state: "done"`). */
export function isAssistantStreamComplete(message: ChatUiMessageLike): boolean {
  const textParts = (message.parts ?? []).filter((p) => p.type === 'text');
  if (textParts.length === 0) {
    return false;
  }
  return textParts.every((p) => {
    if (!('state' in p)) return true;
    const state = p.state;
    return state === undefined || state === 'done';
  });
}

/**
 * Visitor-visible assistant bubble text. While the turn is in flight, returns empty
 * so the UI shows wait chrome instead of flashing raw tokens.
 */
export function formatAssistantBubbleText(
  raw: string,
  displayReady: boolean,
  displayFn: (text: string) => string,
): string {
  if (!displayReady) return '';
  const visible = displayFn(raw).trim();
  if (visible) return visible;
  return FALLBACK_REFUSAL_TEXT;
}
